/**
 * Splits the streamed LawFusion Summary into the sections the consumer page
 * renders as separate coloured blocks.
 *
 * The section list is fixed by the summary system prompt
 * (backend/constants/consumerPrompts/multi-ai-summary.md), which requires the
 * model to emit exactly these headings in this order:
 *
 *   1. Bottom Line
 *   2. What the AIs Agree On
 *   3. What May Depend on Your Situation
 *   4. Possible Options / Next Steps
 *   5. Questions to Consider Asking an Attorney
 *   Disclaimer
 *
 * Matching is deliberately loose — headings are normalised and matched on
 * distinctive words rather than exact strings, so a model that drops the
 * numbering, changes "AIs" to "AI models", or uses a different heading level
 * still lands in the right block. Anything unrecognised is preserved rather
 * than dropped, and a summary that parses to nothing falls back to being
 * rendered whole by the caller.
 */

/** Ordered so the renderer can lay blocks out without re-sorting. */
export const SUMMARY_SECTIONS = [
  "bottomLine",
  "agree",
  "depends",
  "options",
  "attorneyQuestions",
];

/**
 * The blocks as the reader meets them: order, heading and colour tone.
 *
 * Shared by the on-screen renderer (ConsumerSummary) and the PDF builder
 * (consumerSummaryExport) so the download cannot drift from the page — the
 * download is meant to be the same document.
 */
export const SUMMARY_BLOCKS = [
  { key: "bottomLine", tone: "blue", title: "Bottom line" },
  { key: "agree", tone: "green", title: "What the AIs agree on" },
  { key: "depends", tone: "amber", title: "What may depend on your situation" },
  { key: "options", tone: "teal", title: "Possible options / next steps" },
  {
    key: "attorneyQuestions",
    tone: "violet",
    title: "Questions to consider asking an attorney",
  },
];

const HEADING = /^\s{0,3}(?:#{1,6}\s*|\*\*)\s*(?:\d+[.)]\s*)?([^\n*#]+?)\s*(?:\*\*)?\s*:?\s*$/;

/** Normalise a heading for matching: lowercase, alphanumerics and spaces only. */
function normalize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function classify(heading) {
  const h = normalize(heading);
  if (!h) return null;

  // The umbrella title the prompt puts above everything — not a section.
  if (h === "your lawfusion summary" || h === "lawfusion summary") return "title";

  if (h.includes("bottom line")) return "bottomLine";
  if (h.includes("agree")) return "agree";
  if (h.includes("depend")) return "depends";
  if (h.includes("next step") || h.includes("option")) return "options";
  if (h.includes("attorney") && h.includes("question")) return "attorneyQuestions";
  if (h.includes("ask") && h.includes("attorney")) return "attorneyQuestions";
  if (h.includes("disclaimer")) return "disclaimer";
  return null;
}

/**
 * @param {string} markdown - the summary text, possibly still streaming
 * @returns {{sections: Object, disclaimer: string, matched: boolean, preamble: string}}
 *   `matched` is false when nothing recognisable was found, which tells the
 *   caller to fall back to rendering the raw summary.
 */
export function parseConsumerSummary(markdown = "") {
  const text = typeof markdown === "string" ? markdown : "";
  if (!text.trim()) {
    return { sections: {}, disclaimer: "", matched: false, preamble: "" };
  }

  const sections = {};
  let disclaimer = "";
  const preamble = [];

  // Unrecognised headings keep their text with the section they appear under,
  // so nothing the model writes is silently discarded.
  let current = null;
  let buffer = [];

  const commit = () => {
    const body = buffer.join("\n").trim();
    buffer = [];
    if (!body) return;
    if (current === "disclaimer") {
      // The prompt's own example repeats a bold "Disclaimer:" lead-in under the
      // heading; drop it so the label is not rendered twice.
      const cleaned = body.replace(/^\s*(?:\*\*)?disclaimer:?(?:\*\*)?\s*/i, "");
      disclaimer = disclaimer ? `${disclaimer}\n${cleaned}` : cleaned;
    } else if (current && current !== "title") {
      sections[current] = sections[current] ? `${sections[current]}\n${body}` : body;
    } else {
      preamble.push(body);
    }
  };

  for (const line of text.split("\n")) {
    const match = line.match(HEADING);
    const kind = match ? classify(match[1]) : null;

    if (kind) {
      commit();
      current = kind;
      continue;
    }

    // A bolded "Disclaimer:" run in with its paragraph, as the prompt's own
    // example writes it.
    if (current !== "disclaimer" && /^\s*\*\*disclaimer:?\*\*/i.test(line)) {
      commit();
      current = "disclaimer";
      const rest = line.replace(/^\s*\*\*disclaimer:?\*\*\s*/i, "");
      if (rest.trim()) buffer.push(rest);
      continue;
    }

    buffer.push(line);
  }
  commit();

  const matched = SUMMARY_SECTIONS.some((key) => sections[key]);
  return {
    sections,
    disclaimer,
    matched,
    preamble: preamble.join("\n").trim(),
  };
}
